/**
 * Form model + pure conversions for the tournament-series create/edit pages
 * (admin#67). Same discipline as `tournament-form.ts`: the shape rules — what
 * counts as "changed", how a GEL amount becomes `fee_minor` — have one home
 * and are unit-testable without rendering anything.
 *
 * Unlike the one-off form, a series-rule PATCH is never rejected by a
 * frozen-field 409 (api#156's freeze lives at the *occurrence* level, not on
 * the rule row itself — see `AdminTournamentSeriesEditResponse`), so this
 * module's presence-diffing exists to avoid sending no-op fields, not to
 * dodge a 409 the way `tournament-form.ts`'s does.
 *
 * Deliberately carries no registration-deadline field: `prospin-api`'s
 * tournament-series contract has none (`CreateTournamentSeriesRequest` /
 * `PatchTournamentSeriesRequest`, verified against the live `openapi.json`)
 * — "Registration deadline is not a series concept" is an explicit,
 * documented decision in api#156's DECISIONS.md, not an oversight here. A
 * materialized occurrence's own deadline is set afterwards via the existing
 * one-off `PATCH /admin/tournaments/{id}`.
 */
import type {
  CreateTournamentSeriesBody,
  PatchTournamentSeriesBody,
} from './api';
import { feeGelFromMinor, feeMinorFromGel, TOURNAMENT_LOCALES, LOCALE_LABELS } from './tournaments';
import type { AdminTournamentSeries, TournamentLocale, TtarResultInputMode } from './types';

export interface TournamentSeriesTranslationForm {
  title: string;
  description: string;
}

export type TournamentSeriesTranslationsForm = Record<TournamentLocale, TournamentSeriesTranslationForm>;

export interface TournamentSeriesFormValue {
  translations: TournamentSeriesTranslationsForm;
  dayOfWeek: number; // 0 = Monday
  startTime: string; // HH:MM
  endTime: string; // HH:MM (may be 24:00)
  tableIds: number[];
  feeGel: string; // free text; parsed to fee_minor on submit
  maxParticipants: string; // free text, optional
  minTtarRating: string; // free text, optional
  maxTtarRating: string; // free text, optional
  warmupMinutes: string; // free text; parsed to warmup_minutes on submit
  ttarResultInputMode: TtarResultInputMode;
  startsOn: string; // YYYY-MM-DD
  endsOn: string; // optional, YYYY-MM-DD
  imageUrl: string;
}

export const EMPTY_TOURNAMENT_SERIES_FORM: TournamentSeriesFormValue = {
  translations: {
    en: { title: '', description: '' },
    ru: { title: '', description: '' },
    ka: { title: '', description: '' },
  },
  dayOfWeek: 0,
  startTime: '',
  endTime: '',
  tableIds: [],
  feeGel: '',
  maxParticipants: '',
  minTtarRating: '',
  maxTtarRating: '',
  warmupMinutes: '30',
  ttarResultInputMode: 'PlayersWithOrganizerConfirm',
  startsOn: '',
  endsOn: '',
  imageUrl: '',
};

/** Populate the shared form model from a loaded `AdminTournamentSeries` (edit page). */
export function formFromTournamentSeries(s: AdminTournamentSeries): TournamentSeriesFormValue {
  const translations = TOURNAMENT_LOCALES.reduce((acc, locale) => {
    const tr = s.translations[locale];
    acc[locale] = { title: tr?.title ?? '', description: tr?.description ?? '' };
    return acc;
  }, {} as TournamentSeriesTranslationsForm);

  return {
    translations,
    dayOfWeek: s.day_of_week,
    startTime: s.start_time,
    endTime: s.end_time,
    tableIds: [...s.table_ids],
    feeGel: String(feeGelFromMinor(s.fee_minor)),
    maxParticipants: s.max_participants != null ? String(s.max_participants) : '',
    minTtarRating: s.min_ttar_rating != null ? String(s.min_ttar_rating) : '',
    maxTtarRating: s.max_ttar_rating != null ? String(s.max_ttar_rating) : '',
    warmupMinutes: String(s.warmup_minutes),
    ttarResultInputMode: s.ttar_result_input_mode,
    startsOn: s.starts_on,
    endsOn: s.ends_on ?? '',
    imageUrl: s.image_url ?? '',
  };
}

/** Human-readable validation errors, or an empty array when the form can submit. */
export function tournamentSeriesFormErrors(form: TournamentSeriesFormValue): string[] {
  const errors: string[] = [];

  for (const locale of TOURNAMENT_LOCALES) {
    if (!form.translations[locale].title.trim()) {
      errors.push(`${LOCALE_LABELS[locale]} title is required.`);
    }
  }

  if (!form.startTime) errors.push('Start time is required.');
  if (!form.endTime) errors.push('End time is required.');
  if (form.startTime && form.endTime && form.endTime <= form.startTime) {
    errors.push('End time must be after start time.');
  }

  if (form.tableIds.length === 0) errors.push('Select at least one table.');

  const fee = Number(form.feeGel);
  if (form.feeGel.trim() === '' || !Number.isFinite(fee) || fee < 0) {
    errors.push('Fee must be a non-negative number.');
  }

  if (form.maxParticipants.trim() !== '') {
    const cap = Number(form.maxParticipants);
    if (!Number.isInteger(cap) || cap <= 0) {
      errors.push('Capacity must be a positive whole number.');
    }
  }

  const ratingBounds = [form.minTtarRating, form.maxTtarRating];
  const hasInvalidRatingBound = ratingBounds.some((value) => {
    if (value.trim() === '') return false;
    const rating = Number(value);
    return !Number.isInteger(rating) || rating < 1;
  });
  if (hasInvalidRatingBound) {
    errors.push('TTAR rating must be a whole number of at least 1');
  } else if (form.minTtarRating.trim() !== '' && form.maxTtarRating.trim() !== '') {
    if (Number(form.minTtarRating) > Number(form.maxTtarRating)) {
      errors.push("TTAR rating 'from' must not exceed 'to'");
    }
  }

  const warmupMinutes = Number(form.warmupMinutes);
  if (
    form.warmupMinutes.trim() === ''
    || !Number.isInteger(warmupMinutes)
    || warmupMinutes < 0
    || warmupMinutes > 120
  ) {
    errors.push('Warmup must be a whole number of minutes between 0 and 120');
  } else if (form.startTime) {
    const [hours, minutes] = form.startTime.split(':').map(Number);
    if (hours * 60 + minutes < warmupMinutes) {
      errors.push('Warmup cannot start before midnight');
    }
  }

  if (!form.startsOn) errors.push('Series start date is required.');
  if (form.startsOn && form.endsOn && form.endsOn < form.startsOn) {
    errors.push('End date cannot be before the start date.');
  }

  return errors;
}

type TranslationsPayload = Record<TournamentLocale, { title: string; description: string | null }>;

function nullableRating(value: string): number | null {
  return value.trim() === '' ? null : Number(value);
}

function translationsPayload(
  form: TournamentSeriesTranslationsForm,
  locales: readonly TournamentLocale[],
): TranslationsPayload {
  return locales.reduce((acc, locale) => {
    acc[locale] = {
      title: form[locale].title.trim(),
      description: form[locale].description.trim() || null,
    };
    return acc;
  }, {} as TranslationsPayload);
}

/** Build the `POST /admin/tournament-series` body. Caller validates first. */
export function buildCreateTournamentSeriesBody(
  form: TournamentSeriesFormValue,
): CreateTournamentSeriesBody {
  return {
    translations: translationsPayload(form.translations, TOURNAMENT_LOCALES),
    day_of_week: form.dayOfWeek,
    start_time: form.startTime,
    end_time: form.endTime,
    fee_minor: feeMinorFromGel(Number(form.feeGel)),
    table_ids: form.tableIds,
    max_participants: form.maxParticipants.trim() === '' ? null : Number(form.maxParticipants),
    min_ttar_rating: nullableRating(form.minTtarRating),
    max_ttar_rating: nullableRating(form.maxTtarRating),
    warmup_minutes: Number(form.warmupMinutes),
    ttar_result_input_mode: form.ttarResultInputMode,
    starts_on: form.startsOn,
    ends_on: form.endsOn.trim() === '' ? null : form.endsOn,
  };
}

function sameNumberSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const as = [...a].sort((x, y) => x - y);
  const bs = [...b].sort((x, y) => x - y);
  return as.every((v, i) => v === bs[i]);
}

/**
 * Build the `PATCH /admin/tournament-series/{id}` body from what actually
 * changed between the loaded form (`original`) and the edited one
 * (`current`). Presence-diffed for tidiness (avoid no-op fields), not to
 * dodge a freeze — see the module doc for why a series-rule PATCH has none.
 */
export function buildPatchTournamentSeriesBody(
  original: TournamentSeriesFormValue,
  current: TournamentSeriesFormValue,
): PatchTournamentSeriesBody {
  // Preserve sparse PATCH semantics despite codegen marking the API's
  // defaulted fields as required.
  const body: Partial<PatchTournamentSeriesBody> = {};

  const changedLocales = TOURNAMENT_LOCALES.filter((locale) => {
    const a = original.translations[locale];
    const b = current.translations[locale];
    return a.title !== b.title || a.description !== b.description;
  });
  if (changedLocales.length > 0) {
    body.translations = translationsPayload(current.translations, changedLocales);
  }

  if (current.dayOfWeek !== original.dayOfWeek) body.day_of_week = current.dayOfWeek;
  if (current.startTime !== original.startTime) body.start_time = current.startTime;
  if (current.endTime !== original.endTime) body.end_time = current.endTime;
  if (!sameNumberSet(current.tableIds, original.tableIds)) body.table_ids = current.tableIds;

  const currentFeeMinor = feeMinorFromGel(Number(current.feeGel));
  const originalFeeMinor = feeMinorFromGel(Number(original.feeGel));
  if (currentFeeMinor !== originalFeeMinor) body.fee_minor = currentFeeMinor;

  const currentCap = current.maxParticipants.trim() === '' ? null : Number(current.maxParticipants);
  const originalCap = original.maxParticipants.trim() === '' ? null : Number(original.maxParticipants);
  if (currentCap !== originalCap) body.max_participants = currentCap;

  const currentMinTtarRating = nullableRating(current.minTtarRating);
  const originalMinTtarRating = nullableRating(original.minTtarRating);
  if (currentMinTtarRating !== originalMinTtarRating) body.min_ttar_rating = currentMinTtarRating;

  const currentMaxTtarRating = nullableRating(current.maxTtarRating);
  const originalMaxTtarRating = nullableRating(original.maxTtarRating);
  if (currentMaxTtarRating !== originalMaxTtarRating) body.max_ttar_rating = currentMaxTtarRating;

  const currentWarmupMinutes = Number(current.warmupMinutes);
  const originalWarmupMinutes = Number(original.warmupMinutes);
  if (currentWarmupMinutes !== originalWarmupMinutes) body.warmup_minutes = currentWarmupMinutes;

  if (current.ttarResultInputMode !== original.ttarResultInputMode) {
    body.ttar_result_input_mode = current.ttarResultInputMode;
  }

  if (current.startsOn !== original.startsOn) body.starts_on = current.startsOn;

  const currentEnds = current.endsOn.trim() === '' ? null : current.endsOn;
  const originalEnds = original.endsOn.trim() === '' ? null : original.endsOn;
  if (currentEnds !== originalEnds) body.ends_on = currentEnds;

  const currentImg = current.imageUrl.trim() || null;
  const originalImg = original.imageUrl.trim() || null;
  if (currentImg !== originalImg) body.image_url = currentImg;

  return body as PatchTournamentSeriesBody;
}
